import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { FileText, ShieldCheck, IdCard, Upload, Trash2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  uefa: { icon: ShieldCheck, bg: "UEFA сертификат",    en: "UEFA certificate" },
  id:   { icon: IdCard,      bg: "Лична карта",         en: "ID card" },
  cert: { icon: FileText,    bg: "Друг документ",       en: "Other document" },
};

/**
 * Verification document uploader.
 *  - Drag-drop or click to pick file (image or PDF, max 10MB).
 *  - Shows uploaded docs with type chip + filename + remove.
 *  Used in coach/academy settings + admin reviews list (read-only).
 */
export default function VerificationUploader({ lang = "en", className = "", value, onChange, readOnly = false }) {
  const [docs, setDocs] = useState(value || []);
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState("uefa");
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (value) setDocs(value); }, [value]);

  // Reload my docs from server (for accurate state on mount)
  useEffect(() => {
    if (value !== undefined) return;
    api.get("/auth/me").then((r) => setDocs(r.data?.verification_docs || [])).catch(() => {});
  }, [value]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(lang === "bg" ? "Файлът е твърде голям (10MB макс.)" : "File too large (10MB max)"); return; }
    const ok = file.type.startsWith("image/") || file.type === "application/pdf";
    if (!ok) { toast.error(lang === "bg" ? "Само изображения или PDF" : "Only images or PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/verification/upload?type=${type}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = [...docs, data];
      setDocs(next);
      onChange && onChange(next);
      toast.success(lang === "bg" ? "Качено" : "Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
    setBusy(false);
  };

  const remove = async (d) => {
    if (!window.confirm(lang === "bg" ? "Изтрий документа?" : "Remove this document?")) return;
    try {
      await api.delete(`/verification/${d.id}`);
      const next = docs.filter((x) => x.id !== d.id);
      setDocs(next);
      onChange && onChange(next);
    } catch (_) { toast.error("Error"); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    if (readOnly) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  return (
    <div className={className} data-testid="verification-uploader">
      {!readOnly && (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(TYPE_META).map(([k, v]) => {
              const Icon = v.icon;
              const active = type === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setType(k)}
                  className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest border flex items-center gap-1.5 transition-all ${active ? "border-pitch bg-pitch/10 text-pitch" : "border-white/10 text-white/50 hover:border-pitch/40 hover:text-white"}`}
                  data-testid={`verify-type-${k}`}
                >
                  <Icon className="w-3 h-3" /> {lang === "bg" ? v.bg : v.en}
                </button>
              );
            })}
          </div>
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`p-6 border-2 border-dashed cursor-pointer transition-all rounded ${dragging ? "border-pitch bg-pitch/5" : "border-white/15 hover:border-pitch/45 hover:bg-white/[0.02]"}`}
            data-testid="verify-dropzone"
          >
            <div className="flex flex-col items-center justify-center text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${dragging ? "bg-pitch text-white" : "bg-pitch/10 text-pitch"}`}>
                {busy ? <Sparkles className="w-5 h-5 animate-pulse" /> : <Upload className="w-5 h-5" />}
              </div>
              <div className="font-oswald uppercase tracking-tight text-base mt-3">
                {busy
                  ? (lang === "bg" ? "Качване..." : "Uploading...")
                  : (lang === "bg" ? "Пусни файл тук" : "Drop file here")}
              </div>
              <div className="text-[10px] font-mono text-white/40 mt-1 tracking-widest">
                {lang === "bg" ? "или КЛИКНИ за избор · JPG, PNG, PDF до 10MB" : "or CLICK to browse · JPG, PNG, PDF up to 10MB"}
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
              className="hidden"
              data-testid="verify-file-input"
            />
          </div>
        </>
      )}

      {/* Doc list */}
      {docs.length > 0 && (
        <div className="mt-4 space-y-2">
          {docs.map((d) => {
            const meta = TYPE_META[d.type] || TYPE_META.cert;
            const Icon = meta.icon;
            const ext = (d.content_type || "").includes("pdf") ? "PDF" : "IMG";
            return (
              <div key={d.id} className="surface-1 p-3 flex items-center gap-3" data-testid={`verify-doc-${d.id}`}>
                <div className="w-10 h-10 rounded bg-pitch/10 border border-pitch/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-pitch" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-oswald uppercase tracking-tight text-sm">{lang === "bg" ? meta.bg : meta.en}</span>
                    <span className="chip-badge text-[9px]">{ext}</span>
                    <Check className="w-3 h-3 text-pitch" />
                  </div>
                  <div className="text-[10px] font-mono text-white/40 truncate">{d.filename}</div>
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => remove(d)}
                    className="p-1.5 text-white/50 hover:text-red-400 flex-shrink-0"
                    data-testid={`verify-remove-${d.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
